import { useCallback } from 'react';
import { Anything } from 'src/types';
import { usePosition } from './position';

type UseNewAnythingReturn = {
  getPlaceholder: (type: Anything['type']) => [string, string];
  getValues: (
    type: Anything['type']
  ) => Promise<[Anything['value1']?, Anything['value2']?]>;
};

export function useNewAnythingHelpers(): UseNewAnythingReturn {
  const { position } = usePosition();

  const getPlaceholder = useCallback<UseNewAnythingReturn['getPlaceholder']>(
    (type: Anything['type']) => {
      switch (type) {
        case 'IMAGE_URL':
          return ['URL', 'Alt'];
        case 'URL':
          return ['https://...', 'Link name'];
        case 'LENGTH':
          return ['0', 'Unit (km, m, ...)'];
        case 'WEIGHT':
          return ['0', 'Unit (kg, g, ...)'];
        case 'AUTHOR':
          return ['Full name', ''];
        case 'PRICE':
          return ['Amount', 'Currency (kr, ...)'];
        case 'TIME':
          return ['00h00m00s', 'Unit (h, min, sec, ...)'];
        case 'DATE':
          return ['YYYY-mm-dd', 'YYYY-mm-dd / Label'];
        case 'DATETIME':
          return ['YYYY-mm-dd hh:MM:ss', 'Label or YYYY-mm-dd hh:MM:ss'];
        case 'TEXT':
          return ['You can type Anything 🦸🏼‍♂️', ''];
        case 'POSITION':
          return ['Latitude (laddar...)', 'Longitude (laddar...)'];
        default:
          return ['Value 1', 'Value 2 (optional)'];
      }
    },
    []
  );

  const getValues = useCallback<UseNewAnythingReturn['getValues']>(
    (type: Anything['type']) => {
      return new Promise((resolve, reject) => {
        switch (type) {
          case 'IMAGE_URL':
          case 'URL':
          case 'LENGTH':
          case 'WEIGHT':
          case 'AUTHOR':
          case 'PRICE':
          case 'TIME':
          case 'TEXT': {
            resolve([undefined, undefined]);
            return;
          }
          case 'DATE': {
            const now = new Date();
            now.setHours(0);
            now.setMinutes(0);
            now.setSeconds(0);
            return resolve([now.toLocaleDateString('sv'), undefined]);
          }
          case 'DATETIME': {
            const now = new Date();
            const date = now.toLocaleDateString('sv');
            const time = now.toLocaleTimeString('sv');
            return resolve([`${date} ${time}`, undefined]);
          }
          case 'POSITION':
            if (position?.latitude && position?.longitude) {
              resolve([position.latitude, position.longitude]);
            } else {
              console.warn('Unable to determine device position');
              resolve([undefined, undefined]);
            }
            return;
          default:
            return ['Value 1', 'Value 2 (optional)'];
        }
      });
    },
    [position]
  );

  return {
    getPlaceholder,
    getValues,
  };
}
